import { useEffect, useState } from "react";
import Avatar from "../ds/Avatar";
import Badge from "../ds/Badge";
import Button from "../ds/Button";
import IconButton from "../ds/IconButton";
import ProgressBar from "../ds/ProgressBar";
import { ChevronLeftIcon, ChevronRightIcon } from "../ds/icons";
import { PaidOutOf, campaignRateText, daysLeft, formatMoney } from "./campaignDisplay";
import { getParticipantTypeLabel } from "../../utils/campaignCardUtils";
import { getPerPlatformRateRows, getPlatformShortLabel } from "../../utils/campaignRates";

/*
  Discover's Featured row: the campaigns admins picked, one at a time, with
  arrows, a "1 / 3" count and dots. Several take turns every 10 seconds, holding
  only while the mouse is on Join so the card can't change under that click. Each is
  Figma "Featured campaign", Size Full, Style Cover: the cover fills the card
  under a scrim, and the card stays in the dark theme so its text reads on any
  photo. It wraps to two rows when narrow.
*/

const TURN_MS = 10000;

const SCRIM =
  "linear-gradient(180deg, rgba(10,10,12,0.55) 0%, rgba(10,10,12,0) 22%, rgba(10,10,12,0) 100%), " +
  "linear-gradient(180deg, rgba(10,10,12,0) 0%, rgba(10,10,12,0) 40%, rgba(10,10,12,0.6) 75%, rgba(10,10,12,0.9) 100%), " +
  "linear-gradient(90deg, rgba(10,10,12,0.88) 0%, rgba(10,10,12,0.6) 40%, rgba(10,10,12,0.05) 70%, rgba(10,10,12,0) 100%)";

const FeaturedCampaignCard = ({ campaign, onOpen, onHold, language, t }) => {
  const media = campaign.thumbnail || campaign.campaignLogo || "";
  const isVideo = Boolean(media) && (campaign.thumbnailType || "image") === "video";
  const budget = Number(campaign.budget) || 0;
  const paid = Number(campaign.spent) || 0;
  const days = daysLeft(campaign);
  const creators = campaign.creatorCount || 0;
  const organizer = campaign.mintSpace?.name || campaign.createdBy?.businessName || campaign.createdBy?.username || "";

  const meta = [
    days === null ? null : days <= 1 ? t("home.daysLeftOne") : t("home.daysLeft", { count: days }),
    creators > 0 ? t(creators === 1 ? "home.creatorsOne" : "home.creatorsOther", { count: creators }) : null,
  ].filter(Boolean);

  return (
    <article
      data-theme="dark"
      className="relative flex min-h-[368px] flex-col justify-between gap-[14px] overflow-hidden rounded-radius-lg border border-accent-default bg-bg-raised p-space-20 text-label-primary min-[600px]:px-space-32 min-[600px]:pb-space-32 min-[600px]:pt-[28px]"
    >
      <div className="absolute inset-0 bg-fill-quaternary" aria-hidden="true">
        {media &&
          (isVideo ? (
            <video src={media} className="h-full w-full object-cover" muted loop playsInline autoPlay preload="metadata" />
          ) : (
            <img src={media} alt="" className="h-full w-full object-cover" />
          ))}
      </div>
      <div className="absolute inset-0" style={{ backgroundImage: SCRIM }} aria-hidden="true" />

      <div className="relative flex flex-wrap items-center gap-space-8">
        <span className="rounded-full bg-accent-tinted px-space-12 py-space-4 text-caps-label uppercase text-accent-tinted-label">
          {t("home.featuredChip")}
        </span>
        <Badge size="s">{getParticipantTypeLabel(t, campaign.participantType)}</Badge>
        {meta.length > 0 && <span className="text-footnote text-label-secondary">{meta.join(" · ")}</span>}
      </div>

      <div className="relative flex flex-wrap items-end justify-between gap-space-24">
        <div className="flex min-w-0 max-w-[600px] flex-[1_1_320px] flex-col gap-[14px]">
          <h3 className="text-title-1 min-[600px]:text-[34px] min-[600px]:leading-[42px]">{campaign.campaignName}</h3>
          {organizer && (
            <div className="flex min-w-0 items-center gap-space-8">
              <Avatar src={campaign.mintSpace?.logo} name={organizer} />
              <span className="truncate text-footnote text-label-secondary">{organizer}</span>
            </div>
          )}
          <p className="flex items-baseline gap-space-8 whitespace-nowrap">
            <span className="text-title-2">{campaignRateText(campaign, language)}</span>
            <span className="text-subheadline text-label-secondary">{t("home.perThousandViews")}</span>
          </p>
          {getPerPlatformRateRows(campaign).some((row) => row.key) && (
            <div className="flex flex-wrap items-center gap-space-8">
              {getPerPlatformRateRows(campaign)
                .filter((row) => row.key)
                .map((row) => (
                  <Badge key={row.key} size="s">
                    {getPlatformShortLabel(row.key)} · {formatMoney(row.rate, language)}
                  </Badge>
                ))}
            </div>
          )}
        </div>

        <div className="flex w-full flex-col gap-[14px] min-[600px]:w-[300px]">
          {budget > 0 && (
            <div className="flex flex-col gap-space-8">
              <div className="flex items-center justify-between gap-space-8 whitespace-nowrap">
                <span className="text-caps-label uppercase text-label-tertiary">{t("home.budget")}</span>
                <PaidOutOf paid={paid} total={budget} language={language} t={t} />
              </div>
              <ProgressBar value={paid / budget} />
            </div>
          )}
          <div onPointerEnter={(event) => event.pointerType === "mouse" && onHold(true)} onPointerLeave={() => onHold(false)}>
            <Button fullWidth onClick={() => onOpen(campaign)}>
              {t("home.joinCampaign")}
            </Button>
          </div>
        </div>
      </div>
    </article>
  );
};

const FeaturedCampaigns = ({ campaigns, onOpen, language, t }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = campaigns.length;

  // A shorter list (an admin took one out) never leaves the index past the end.
  useEffect(() => {
    if (index >= count) setIndex(0);
  }, [index, count]);

  // Waits on `index` too, so pressing an arrow gives that campaign its full 10 seconds.
  useEffect(() => {
    if (count < 2 || paused) return undefined;
    const timer = setTimeout(() => setIndex((i) => (i + 1) % count), TURN_MS);
    return () => clearTimeout(timer);
  }, [index, count, paused]);

  if (count === 0) return null;
  const current = campaigns[Math.min(index, count - 1)];
  const step = (by) => setIndex((i) => (i + by + count) % count);

  return (
    <section aria-roledescription="carousel" aria-label={t("home.featuredTitle")} className="flex flex-col gap-space-16">
      <div className="flex items-center justify-between gap-space-12">
        <h2 className="text-caps-section-title uppercase text-label-primary">{t("home.featuredTitle")}</h2>
        {count > 1 && (
          <div className="flex shrink-0 items-center gap-space-8">
            <span className="whitespace-nowrap text-footnote text-label-secondary">
              {index + 1} / {count}
            </span>
            <IconButton label={t("common.previous")} onClick={() => step(-1)}>
              <ChevronLeftIcon />
            </IconButton>
            <IconButton label={t("common.next")} onClick={() => step(1)}>
              <ChevronRightIcon />
            </IconButton>
          </div>
        )}
      </div>

      <div key={current._id} className="animate-fade-in">
        <FeaturedCampaignCard campaign={current} onOpen={onOpen} onHold={setPaused} language={language} t={t} />
      </div>

      {count > 1 && (
        <div className="flex justify-center gap-[6px]" aria-hidden="true">
          {campaigns.map((campaign, i) => (
            <span
              key={campaign._id}
              className={`h-[6px] rounded-full transition-all ${i === index ? "w-space-16 bg-accent-default" : "w-[6px] bg-fill-tertiary"}`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default FeaturedCampaigns;
